// src/modules/requerimiento/views/mantenimiento/Programacion.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Alert, Card, CardContent, Table, TableBody,
  TableCell, TableRow, TableHead, CircularProgress, Stack, Divider,
  Checkbox, Button, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { useParams } from "react-router-dom";
import RequerimientoHeader   from "../../components/RequerimientoHeader";
import { getMantenimientoAsignados, getModemsByRequerimientoPlacas, postAsignarModems } from "../../services/mantenimiento";
import { updateRequerimiento } from "../../services/reqSolicitudes";

export default function MantenimientoProgramacion(props) {
  const params = useParams();
  const cod    = useMemo(() => props?.cod ?? params?.cod, [props?.cod, params?.cod]);

  // ── Asignados ────────────────────────────────────────────
  const [asignados, setAsignados] = useState([]); // Antes quizás estaba como null
  const [loadingAsignados, setLoadingAsignados] = useState(false);
  const [errAsignados, setErrAsignados] = useState(null);

  const fetchAsignados = useCallback(async () => {
    if (!cod) return;
    
    setLoadingAsignados(true);
    setErrAsignados(null);
    
    try {
      const response = await getMantenimientoAsignados(cod);
      
      // LOG DE CONTROL: Mira esto en la consola del navegador (F12)
      console.log("Respuesta API Asignados:", response);
  
      // VALIDACIÓN PROFESIONAL:
      // Si usas el interceptor de tus estándares, la data real está en response.data
      // Si NO usas interceptor, está en response.data.data
      const datosRecuperados = response.data || response; 
  
      if (Array.isArray(datosRecuperados)) {
        setAsignados(datosRecuperados);
      } else if (response.success && Array.isArray(response.data)) {
        setAsignados(response.data);
      } else {
        console.warn("Estructura de datos inesperada:", response);
        setAsignados([]);
      }
      
    } catch (err) {
      console.error("Error en fetchAsignados:", err);
      setErrAsignados(err?.response?.data?.message || "Error al obtener equipos asignados");
      setAsignados([]); // Reset para evitar renderizar basura
    } finally {
      setLoadingAsignados(false);
    }
  }, [cod]);
  
  // 2. Efecto para disparar la carga
  useEffect(() => {
    fetchAsignados();
  }, [fetchAsignados]);

  // ── Módems por placas ─────────────────────────────────────
  const [placasResult,   setPlacasResult]   = useState([]);
  const [placasConsulta, setPlacasConsulta] = useState([]);
  const [loadingPlacas,  setLoadingPlacas]  = useState(false);
  const [errPlacas,      setErrPlacas]      = useState(null);

  const fetchByPlacas = useCallback(async () => {
    if (!cod) { setPlacasResult([]); setPlacasConsulta([]); setErrPlacas(null); return; }
    setLoadingPlacas(true); setErrPlacas(null);
    try {
      const { data } = await getModemsByRequerimientoPlacas(cod);
      setPlacasConsulta(Array.isArray(data?.placas) ? data.placas : []);
      setPlacasResult(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      setErrPlacas(e?.response?.data?.error || e.message || "Error buscando por placas");
      setPlacasConsulta([]);
      setPlacasResult([]);
    } finally { setLoadingPlacas(false); }
  }, [cod]);

  useEffect(() => { fetchByPlacas(); }, [fetchByPlacas]);

  // ── Selección checkbox ────────────────────────────────────
  const [selectedSims, setSelectedSims] = useState(new Set());

  useEffect(() => {
    const valid = new Set(placasResult.map((r) => r.num_sim));
    setSelectedSims((prev) => new Set([...prev].filter((v) => valid.has(v))));
  }, [placasResult]);

  const allVisibleNumSim = useMemo(() => placasResult.map((r) => r.num_sim), [placasResult]);
  const isAllSelected    = allVisibleNumSim.length > 0 && allVisibleNumSim.every((n) => selectedSims.has(n));
  const isIndeterminate  = selectedSims.size > 0 && !isAllSelected;

  const handleToggleOne = (num_sim) => {
    setSelectedSims((prev) => { const next = new Set(prev); next.has(num_sim) ? next.delete(num_sim) : next.add(num_sim); return next; });
  };
  const handleToggleAll = () => {
    setSelectedSims(isAllSelected ? new Set() : new Set(allVisibleNumSim));
  };

  // ── Asignar ───────────────────────────────────────────────
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignErr,     setAssignErr]     = useState(null);
  const [assignOk,      setAssignOk]      = useState(null);

  const handleAsignar = async () => {
    const num_sim = Array.from(selectedSims);
    if (!cod)            { setAssignErr("Falta el código del requerimiento."); return; }
    if (!num_sim.length) { setAssignErr("Selecciona al menos un módem."); return; }
    setAssignLoading(true); setAssignErr(null); setAssignOk(null);
    try {
      await postAsignarModems(String(cod), num_sim);
      setAssignOk(`${num_sim.length} módem(s) asignados al requerimiento ${cod}.`);
      setSelectedSims(new Set());
      await fetchAsignados();
      await props?.onRefreshRequerimiento?.();
      await fetchByPlacas();
    } catch (e) {
      setAssignErr(e?.response?.data?.error || e.message || "No se pudo asignar los módems.");
    } finally { setAssignLoading(false); }
  };

  // ── Finalizar — Dialog en lugar de window.confirm ─────────
  const [openFinalizar,  setOpenFinalizar]  = useState(false);
  const [finalizeLoading,setFinalizeLoading]= useState(false);
  const [finalizeErr,    setFinalizeErr]    = useState(null);
  const [finalizeOk,     setFinalizeOk]     = useState(null);

  const handleFinalizar = async () => {
    if (!cod) { setFinalizeErr("Falta el código del requerimiento."); return; }
    setFinalizeLoading(true); setFinalizeErr(null); setFinalizeOk(null);
    try {
      await updateRequerimiento(String(cod), { estado: 3 });
      // FIX: mensaje corregido — estado: 3 = Trabajo de campo, no estado 7
      setFinalizeOk("Requerimiento avanzado a Trabajo de campo.");
      setOpenFinalizar(false);
      await props?.onRefreshRequerimiento?.();
    } catch (e) {
      setFinalizeErr(e?.response?.data?.error || e.message || "No se pudo actualizar el estado.");
    } finally { setFinalizeLoading(false); }
  };

  const hasAsignados = asignados.length > 0;
  const hasChecks    = selectedSims.size > 0;

  return (
    <Box className="p-4" sx={{ pb: 6 }}>
      {assignOk   && <Alert severity="success" onClose={() => setAssignOk(null)}   sx={{ mb: 2 }}>{assignOk}</Alert>}
      {assignErr  && <Alert severity="error"   onClose={() => setAssignErr(null)}  sx={{ mb: 2 }}>{assignErr}</Alert>}
      {finalizeOk && <Alert severity="success" onClose={() => setFinalizeOk(null)} sx={{ mb: 2 }}>{finalizeOk}</Alert>}
      {finalizeErr && <Alert severity="error"  onClose={() => setFinalizeErr(null)}sx={{ mb: 2 }}>{finalizeErr}</Alert>}

      <RequerimientoHeader key={props?.refreshKey} cod={cod} />

      {/* Asignados */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Asignados al requerimiento</Typography>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          {loadingAsignados ? (
            <Stack direction="row" alignItems="center" gap={1}><CircularProgress size={20} /> Cargando asignados...</Stack>
          ) : errAsignados ? (
            <Alert severity="error">{errAsignados}</Alert>
          ) : asignados.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No hay equipos asignados todavía.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["#","Num. SIM","Placa","Estado","Observación"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {asignados.map((row, idx) => (
                  // FIX: key con num_sim en lugar de índice compuesto
                  <TableRow key={row.num_sim}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{row.num_sim}</TableCell>
                    <TableCell>{row.placa || "—"}</TableCell>
                    <TableCell>{row.estado || "—"}</TableCell>
                    <TableCell>{row.observacion || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />

      {/* Módems por placas */}
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Módems detectados por placas</Typography>
        <Button variant="contained" onClick={handleAsignar} disabled={assignLoading || selectedSims.size === 0}>
          {assignLoading ? "Asignando..." : "Asignar"}
        </Button>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          {!cod ? (
            <Alert severity="info">No hay requerimiento seleccionado.</Alert>
          ) : loadingPlacas ? (
            <Stack direction="row" alignItems="center" gap={1}><CircularProgress size={20} /> Consultando módems por placas...</Stack>
          ) : errPlacas ? (
            <Alert severity="error">{errPlacas}</Alert>
          ) : placasResult.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No se encontraron módems para: {placasConsulta.length ? placasConsulta.join(", ") : "las placas del requerimiento"}.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox indeterminate={isIndeterminate} checked={isAllSelected}
                      onChange={handleToggleAll} inputProps={{ "aria-label": "Seleccionar todos" }} />
                  </TableCell>
                  {/* FIX: columnas cod_servicio y cod_serv_ypfb marcadas como legacy */}
                  {["#","Num. SIM","Placa","Modelo","Ciudad","Estado","N° Chip"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {placasResult.map((row, idx) => (
                  <TableRow key={row.num_sim} hover>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedSims.has(row.num_sim)}
                        onChange={() => handleToggleOne(row.num_sim)}
                        inputProps={{ "aria-label": `Seleccionar ${row.num_sim}` }} />
                    </TableCell>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{row.num_sim}</TableCell>
                    <TableCell>{row.placa || "—"}</TableCell>
                    <TableCell>{row.modelo || "—"}</TableCell>
                    <TableCell>{row.ciudad || "—"}</TableCell>
                    <TableCell>{row.estado || "—"}</TableCell>
                    <TableCell>{row.numero_chip || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Botón finalizar sticky */}
      {hasAsignados && (
        <Box sx={{ position: "fixed", right: { xs: 16, sm: 24 }, bottom: { xs: 16, sm: 24 }, zIndex: (t) => t.zIndex.drawer + 1 }}>
          <Tooltip title={hasChecks ? "Desmarca los checkboxes para finalizar" : ""}>
            <span>
              <Button variant="contained" color="secondary"
                onClick={() => setOpenFinalizar(true)}
                disabled={finalizeLoading || hasChecks}
                sx={{ boxShadow: 3 }}>
                {finalizeLoading ? "Guardando..." : "Finalizar selección"}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* FIX: Dialog en lugar de window.confirm */}
      <Dialog open={openFinalizar} onClose={() => !finalizeLoading && setOpenFinalizar(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Finalizar selección</DialogTitle>
        <DialogContent>
          <Typography>¿Finalizar selección de equipos? El requerimiento avanzará a <strong>Trabajo de campo</strong>.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFinalizar(false)} disabled={finalizeLoading}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={handleFinalizar} disabled={finalizeLoading}>
            {finalizeLoading ? "Guardando..." : "Finalizar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
